import ReactMarkdown from 'react-markdown';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';

type MarkdownContentProps = {
  markdown: string;
};

export function MarkdownContent({ markdown }: MarkdownContentProps) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[
        rehypeSlug,
        [rehypeAutolinkHeadings, { behavior: 'append' }],
      ]}
      components={{
        h1: ({ ...props }) => (
          <h1
            className="mb-6 text-[24px] font-bold tracking-tight"
            style={{ color: 'var(--color-text-primary)' }}
            {...props}
          />
        ),
        h2: ({ ...props }) => (
          <h2
            className="mt-8 mb-3 text-[16px] font-bold"
            style={{ color: 'var(--color-text-primary)' }}
            {...props}
          />
        ),
        h3: ({ ...props }) => (
          <h3
            className="mt-6 mb-2 text-[14px] font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
            {...props}
          />
        ),
        p: ({ ...props }) => (
          <p
            className="mb-4 text-[13px] leading-[1.8]"
            style={{ color: 'var(--color-text-secondary)' }}
            {...props}
          />
        ),
        a: ({ ...props }) => (
          <a
            className="font-medium underline underline-offset-4 hover:opacity-80"
            style={{ color: 'var(--color-accent)' }}
            target="_blank"
            rel="noreferrer"
            {...props}
          />
        ),
        ul: ({ ...props }) => (
          <ul
            className="mb-4 list-disc space-y-2 pl-6 text-[13px] leading-[1.8]"
            style={{ color: 'var(--color-text-secondary)' }}
            {...props}
          />
        ),
        ol: ({ ...props }) => (
          <ol
            className="mb-4 list-decimal space-y-2 pl-6 text-[13px] leading-[1.8]"
            style={{ color: 'var(--color-text-secondary)' }}
            {...props}
          />
        ),
        blockquote: ({ ...props }) => (
          <blockquote
            className="my-6 border-l-4 px-4 py-3 italic"
            style={{
              borderColor: 'var(--color-border-strong)',
              backgroundColor: 'var(--color-bg-elevated)',
              color: 'var(--color-text-secondary)',
            }}
            {...props}
          />
        ),
        table: ({ ...props }) => (
          <div className="my-6 overflow-x-auto">
            <table
              className="w-full min-w-[560px] border-collapse text-left"
              {...props}
            />
          </div>
        ),
        th: ({ ...props }) => (
          <th
            className="px-4 py-3 text-sm font-semibold"
            style={{
              borderBottom: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-bg-elevated)',
              color: 'var(--color-text-primary)',
            }}
            {...props}
          />
        ),
        td: ({ ...props }) => (
          <td
            className="px-4 py-3 text-sm"
            style={{
              borderBottom: '1px solid var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
            {...props}
          />
        ),
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}
